// ─────────────────────────────────────────────────────────────
// Clickbait ERP — Audit Service  v2.0
//
// Writes to the backend POST /api/audit endpoint (Supabase).
// Always also keeps a local localStorage backup so no entry is
// lost if the server is momentarily unreachable.
// ─────────────────────────────────────────────────────────────
import type { AuditEntry } from '../types/auth.types'

const AUDIT_KEY  = 'nexus_audit_log'
const MAX_LOCAL  = 500

// ── Write ─────────────────────────────────────────────────────
export function logAuditEvent(entry: Omit<AuditEntry, 'id' | 'timestamp'>): void {
  const full: AuditEntry = {
    ...entry,
    id:        `aud-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: Date.now(),
  }

  // 1. Always back up to localStorage immediately (never blocks the UI)
  _appendLocal(full)

  // 2. Fire-and-forget to the backend for persistence in Supabase
  fetch('/api/audit', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      userId:   full.userId,
      userName: full.userName,
      userRole: full.userRole,
      action:   full.action,
      module:   full.module,
      detail:   full.detail,
    }),
  }).catch(() => { /* server unavailable — localStorage backup is sufficient */ })
}

function _appendLocal(entry: AuditEntry): void {
  try {
    const raw = localStorage.getItem(AUDIT_KEY)
    const log: AuditEntry[] = raw ? JSON.parse(raw) : []
    log.push(entry)
    const trimmed = log.length > MAX_LOCAL ? log.slice(log.length - MAX_LOCAL) : log
    localStorage.setItem(AUDIT_KEY, JSON.stringify(trimmed))
  } catch { /* storage full or unavailable */ }
}

// ── Read ──────────────────────────────────────────────────────
export function getAuditLog(): AuditEntry[] {
  try {
    const raw = localStorage.getItem(AUDIT_KEY)
    return raw ? (JSON.parse(raw) as AuditEntry[]).reverse() : []   // newest first
  } catch { return [] }
}

// ── Clear (super admin only) ──────────────────────────────────
export function clearAuditLog(): void {
  localStorage.removeItem(AUDIT_KEY)
}

// ── Helpers ───────────────────────────────────────────────────
export function auditLogin(userId: string, userName: string, userRole: string): void {
  logAuditEvent({ userId, userName, userRole: userRole as AuditEntry['userRole'], action: 'LOGIN', module: 'Auth', detail: `${userName} signed in` })
}

export function auditLogout(userId: string, userName: string, userRole: string): void {
  logAuditEvent({ userId, userName, userRole: userRole as AuditEntry['userRole'], action: 'LOGOUT', module: 'Auth', detail: `${userName} signed out` })
}

export function auditImpersonateStart(adminId: string, adminName: string, targetName: string): void {
  logAuditEvent({ userId: adminId, userName: adminName, userRole: 'superadmin', action: 'IMPERSONATE_START', module: 'Admin', detail: `Admin started impersonating ${targetName}` })
}

export function auditImpersonateStop(adminId: string, adminName: string, targetName: string): void {
  logAuditEvent({ userId: adminId, userName: adminName, userRole: 'superadmin', action: 'IMPERSONATE_STOP', module: 'Admin', detail: `Admin stopped impersonating ${targetName}` })
}
